import {useEffect, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {actionCreators, type State} from '@App/state'
import {bindActionCreators} from "@reduxjs/toolkit";
import type {Brand, PremiumService, Service} from "@App/types";
import {changeOnlyManufacturerFilter,} from "@App/state/action-creators";
import {apolloClient} from "@App/graphql/client";
import {
    GET_ORGANIZATION_PREMIUM_SERVICES,
    type OrganizationPremiumServicesQuery
} from "@App/graphql/queries/getOrganizationPremiumServices";
import {useNavigate} from "react-router-dom";
import {useServices} from "@App/hooks/api/useServices";
import {Services, SortOrderType} from "@App/types/enums";
import {OrganizationIsDeutscheLeasing, OrganizationIsMobilityConcept} from "@App/utils/conditionals/organization";
import {PremiumServiceIsReplacementOrPickup} from "@App/utils/conditionals/premiumServices";

export const useHome = () => {

    const [currentStep, setCurrentStep] = useState(0)
    const organization = useSelector((state: State) => state.orders.organization)
    const service = useSelector((state: State) => state.orders.service)
    const place = useSelector((state: State) => state.orders.place)
    const bevSupport = useSelector((state: State) => state.filters.bevSupport)
    const brand = useSelector((state: State) => state.orders.brand)
    const modalDismissed = useSelector((state: State) => state.filters.modalDismissed)
    const premiumServices = useSelector((state: State) => state.orders.premiumServices)
    const [availableSubservices, setAvailableSubservices] = useState<PremiumService[] | any>([])
    const [availableSubservicesLoading, setAvailableComfortservicesLoading] = useState<boolean>(false)
    const [availableComfortservices, setAvailableComfortservices] = useState<PremiumService[] | any>([])
    const [availableComfortservicesLoading, setAvailableSubservicesLoading] = useState<boolean>(false)
    const [showSecondaryTireStep, setShowSecondaryTireStep] = useState(false)
    const [tireStepSelection, setTireStepSelection] = useState('')
    const [secondaryTireStepSelection, setSecondaryTireStepSelection] = useState('')
    const commercialVehicleSupport = useSelector((state: State) => state.filters.commercialVehicleSupport)
    const [autoabo, setAutoabo] = useState(false)
    const numberPlate = useSelector((state: State) => state.orders.numberPlate)
    const mileage = useSelector((state: State) => state.orders.mileage)


    const navigate = useNavigate()

    const {services} = useServices()

    const dispatch = useDispatch();
    const {
        chooseService,
        chooseBrand,
        addPremiumService,
        removePremiumService,
        changeBrandFilter,
        changeBevSupportFilter,
        changeSortByFilter,
        changeCommercialVehicleSupportFilter,
        changeShowAsNonBookableFilter,
    } = bindActionCreators(actionCreators, dispatch)

    useEffect(() => {
        changeSortByFilter(organization.settings.garage_list.default_sorting ?? SortOrderType.SMALLEST_DISTANCE)
    }, [])

    const selectBrand = (selectedBrand: Brand): void => {
        chooseBrand(selectedBrand)
        selectedBrand && setCurrentStep(currentStep + 1)
    }

    const selectEngine = (bev: boolean): void => {
        changeBevSupportFilter(bev)
        if(bev === false){
            chooseBrand(null);
            changeBrandFilter(null);
        }
        setCurrentStep(currentStep + 1)
    }

    const selectEngineAndService = (bev: boolean, serviceId): void => {
        changeBevSupportFilter(bev)
        if(bev === false){
            chooseBrand(null);
            changeBrandFilter(null);
        }
        if(serviceId !== service.id){
            selectService(services.find(service => service.id === serviceId), false);
        }
        setCurrentStep(currentStep + 1)
    }

    const selectCommercialVehicle = (commercialVehicleSupport: boolean): void => {
        changeCommercialVehicleSupportFilter(commercialVehicleSupport)
        setCurrentStep(currentStep + 1)
    }


    const togglePremiumService = (premiumService: PremiumService, premiumServices) => {
        // try to find the service to remove it from the list if already checked
        let exists = premiumServices.some(p => p.id === premiumService.id)

        if (exists) {
            removePremiumService(premiumService)
            return
        }

        // add the premium service to the list if nothing found
        addPremiumService(premiumService)
    }

    const togglePremiumServiceCarReplacementOrPickup = (premiumService: PremiumService, premiumServices) => {
        // try to find the service to remove it from the list if already checked
        let exists = premiumServices.some(p => p.id === premiumService.id)


        premiumServices.forEach((premiumService) => {
            if(PremiumServiceIsReplacementOrPickup(premiumService)){
                removePremiumService(premiumService)
            }
        })

        if (exists) {
            removePremiumService(premiumService)
            return
        }

        // add the premium service to the list if nothing found
        addPremiumService(premiumService)
    }

    const toggleSingularPremiumService = (premiumService: PremiumService, premiumServices) => {
        // try to find the service to remove it from the list if already checked
        let exists = premiumServices.some(p => p.id === premiumService.id)

        if (exists) {
            removePremiumService(premiumService)
            return
        }

        premiumServices.forEach((premiumService) => {
                removePremiumService(premiumService)
        })

        // add the premium service to the list if nothing found
        addPremiumService(premiumService)
    }

    const toggled = (premiumService: PremiumService, premiumServices): boolean => {
        return premiumServices.some(p => p.id === premiumService.id)
    }
    const serviceToggled = (selectedService: Service): boolean => {
        return selectedService.id === service?.id
    }

    const nextStep = ():void => {
        setCurrentStep(currentStep + 1)
    }


    const getAvailableSubServices = (serviceId) => {
        setAvailableSubservicesLoading(true);
        apolloClient.query<OrganizationPremiumServicesQuery>({
            query: GET_ORGANIZATION_PREMIUM_SERVICES, variables:
                {
                    organization: organization.id,
                    services: [serviceId],
                },
        }).then((res) => {
            const subsServices = res.data.organization.premium_services.filter(premiumService => premiumService.service_type === 'sub-service')

            setAvailableSubservices(subsServices);
            setAvailableSubservicesLoading(false);
        })
    }

    const getAvailableComfortServices = (serviceId) => {
        setAvailableComfortservicesLoading(true);
        apolloClient.query<OrganizationPremiumServicesQuery>({
            query: GET_ORGANIZATION_PREMIUM_SERVICES, variables:
                {
                    organization: organization.id,
                    services: [serviceId],
                },
        }).then((res) => {
            const subsServices = res.data.organization.premium_services.filter(premiumService => premiumService.service_type === 'comfort-service')

            setAvailableComfortservices(subsServices);
            setAvailableComfortservicesLoading(false);
        })
    }

    const selectService = (selectedService: Service, resetOtherParams: boolean): void => {
        if (selectedService.pivot.external_url) {
            if(OrganizationIsDeutscheLeasing(organization)) {
                window.open(selectedService.pivot.external_url + '?' + new URLSearchParams(window.location.search), '_self')
            } else {
                window.open(selectedService.pivot.external_url, '_blank')
            }
            return
        }
        if (selectedService.id === Services.VEHICLEDELIVERY) {
            navigate('fahrzeuglieferung')
        }

        setAutoabo(selectedService.id === Services.AUTOABO)
        OrganizationIsMobilityConcept(organization) ? changeOnlyManufacturerFilter(false) : '';
        changeShowAsNonBookableFilter(false);
        if(resetOtherParams){
            chooseBrand(null);
            changeBrandFilter(null);
            changeBevSupportFilter(false);
            changeCommercialVehicleSupportFilter(false);
        }
        getAvailableSubServices(selectedService.id);
        getAvailableComfortServices(selectedService.id);
        premiumServices?.forEach((premiumService) => {
            removePremiumService(premiumService)
        })
        chooseService(selectedService)
        setCurrentStep(currentStep + 1)

        var searchParams = new URLSearchParams(window.location.search)
        searchParams.set('service', selectedService.id.toString());
        var newRelativePathQuery = window.location.pathname + '?' + searchParams.toString();
        history.replaceState(null, '', newRelativePathQuery);

        if(typeof(ga) === 'function'){
            ga('send', 'event', 'select service', selectedService.name, organization.id);
        }

        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
            'event': 'select service',
            'service': selectedService,
            'organization': organization.id
        });
    }

    const simpleServiceSelect = (selectedService: Service): void => {
        if(serviceToggled(selectedService)){
            chooseService(null)
            return
        }
        setAutoabo(selectedService.id === Services.AUTOABO)
        changeShowAsNonBookableFilter(false);
        chooseService(selectedService)

        var searchParams = new URLSearchParams(window.location.search)
        searchParams.set('service', selectedService.id.toString());
        var newRelativePathQuery = window.location.pathname + '?' + searchParams.toString();
        history.replaceState(null, '', newRelativePathQuery);

        if(typeof(ga) === 'function'){
            ga('send', 'event', 'select service', selectedService.name, organization.id);
        }

        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
            'event': 'select service',
            'service': selectedService,
            'organization': organization.id
        });
    }


    const selectTireServiceTypeAndService = (showAsNonBookable: boolean, givenService, showSecondaryTireStep: boolean, tireStepSelection: string, secondaryStep: boolean): void => {
        setShowSecondaryTireStep(showSecondaryTireStep);
        if(secondaryStep){
            setSecondaryTireStepSelection(tireStepSelection);
        }else {
            setTireStepSelection(tireStepSelection);
        }
        selectService(services.find(service => service.id === givenService.id), false);
        changeShowAsNonBookableFilter(showAsNonBookable);
    }

    const stepUp = () => {
        setCurrentStep(currentStep + 1)
    }

    const stepToOneAndResetOthers = () => {
        chooseBrand(null);
        changeBrandFilter(null);
        changeBevSupportFilter(false);
        changeCommercialVehicleSupportFilter(false);
        setCurrentStep(1)
    }

    const hasAKFContractTypeUrlParam = (urlParams: URLSearchParams): boolean => {
        return urlParams.has('akfcontracttype');
    }

    return {
        currentStep,
        setCurrentStep,
        selectBrand,
        selectEngine,
        selectEngineAndService,
        selectCommercialVehicle,
        togglePremiumService,
        togglePremiumServiceCarReplacementOrPickup,
        toggled,
        nextStep,
        organization,
        service,
        premiumServices,
        availableSubservices,
        availableComfortservices,
        availableComfortservicesLoading,
        place,
        bevSupport,
        brand,
        modalDismissed,
        setAvailableSubservices,
        availableSubservicesLoading,
        setAvailableSubservicesLoading,
        selectService,
        selectTireServiceTypeAndService,
        secondaryTireStepSelection,
        tireStepSelection,
        showSecondaryTireStep,
        commercialVehicleSupport,
        autoabo,
        stepUp,
        hasAKFContractTypeUrlParam,
        toggleSingularPremiumService,
        stepToOneAndResetOthers,
        numberPlate,
        mileage,
        simpleServiceSelect,
        serviceToggled
    }
}
